import { inject } from '@angular/core';
import { map } from 'rxjs';
import { OrderStatus } from '@shared/models';
import { PaletteLookup, ShortcutsService } from '@shared/services/shortcuts.service';
import { toStatusLabel } from '@shared/utils';
import { ServicingTransferService } from './servicing-transfer.service';

const ORDERS_URL = '/products/servicing-transfer/orders';

/**
 * Command palette: a typed number is looked up as a Servicing Transfer order (for users who have the
 * product), so the palette shows its name and status, or says there is no such order, before opening it.
 * Registered at startup (app.config.ts); call in an injection context.
 */
export function servicingTransferOrderLookup(): PaletteLookup {
  const shortcuts = inject(ShortcutsService);
  const orders = inject(ServicingTransferService);
  return {
    title: 'Order',
    matches: (query) =>
      /^\d{1,9}$/.test(query) && shortcuts.navigation().some((entry) => entry.path === ORDERS_URL),
    find: (query) =>
      orders.getOrders({ page: 1, pageSize: 1, filters: `id=${query}` }).pipe(
        map(({ items: [order] }) =>
          order
            ? {
                label: `Order ${query} · ${order.name}`,
                context: toStatusLabel(OrderStatus, order.status),
                icon: 'open_in_new',
                url: `${ORDERS_URL}/${query}`
              }
            : null
        )
      ),
    missing: (query) => `No order ${query} for this client. Check the number.`,
    fallback: (query) => ({
      label: `Open order ${query}`,
      context: 'Couldn’t check it',
      icon: 'open_in_new',
      url: `${ORDERS_URL}/${query}`
    })
  };
}
